"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { BlockConfig } from "@/lib/registry/types";
import { cn } from "@/lib/utils";
import {
  Eye,
  Star,
  Laptop,
  Image as ImageIcon,
  ChevronUp,
  ChevronDown,
  Heart,
} from "lucide-react";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  useDeferredValue,
} from "react";
import { FixedSizeGrid } from "react-window";
import useMeasure from "react-use-measure";
import { FilterControls } from "./filter-controls";
import { type ExclusiveMode, FilterHeader } from "./filter-header";
import type { ComponentPreviewData } from "./page-wrapper";
import { getDynamicDemoComponent } from "@/app/(app)/components/_utils/load-demo";
import {
  getLibraryBgColor,
  getLibraryColor,
  formatLibraryName,
} from "@/lib/colors/library-colors";
import { Logo } from "@/components/images/logo";
import { routes } from "@/config/routes";
import { ComponentPreview } from "@/app/(app)/components/_components/component-preview";
import { motion, useInView } from "framer-motion";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// Extracted Hooks
import { useStarredItems } from "./use-starred-items";
import { useComponentFilters } from "./use-component-filters";
import { useGridCalculations } from "./use-grid-calculations";
import { useScrollCollapse } from "./use-scroll-collapse";

// Extracted Components
import {
  MemoizedComponentCard as ComponentCard,
  type Item,
} from "./component-card";
import { MemoizedComponentBrowserHeader as ComponentBrowserHeader } from "./component-browser-header";
import { SuspenseFallback } from "@/components/primitives/suspense-fallback";

// Re-add VirtualizedGridProps interface
interface VirtualizedGridProps {
  initialFilter?: {
    libraries: string[];
    categories: string[];
  };
  items?: BlockConfig[]; // Original items from props
  initiallyScrolled?: boolean;
  alwaysScrolled?: boolean;
  initiallyFullyCollapsed?: boolean;
  preRenderedPreviews: { [key: string]: ComponentPreviewData };
}

export default function VirtualizedGrid({
  initialFilter,
  items = [],
  initiallyScrolled = false,
  alwaysScrolled = false,
  initiallyFullyCollapsed = false,
  preRenderedPreviews,
}: VirtualizedGridProps) {
  // Global state for showing demos vs images
  const [showDemosGlobally, setShowDemosGlobally] = useState<boolean>(false);

  // Prepare initial items with unique IDs
  const initialItems: Item[] = useMemo(() => {
    // Add type BlockConfig to item parameter
    return items.map((item: BlockConfig) => ({
      ...item,
      id: `${item.library}/${item.name}`,
    }));
  }, [items]);

  // --- Hooks --- //

  // Starred items management
  const { toggleStar, isStarred, starredItemsSet } =
    useStarredItems(initialItems);

  // Filtering and sorting logic
  const {
    searchQuery,
    activeLibraries,
    activeCategories, // Get activeCategories here
    sortOption,
    showStarredOnly,
    allLibraries,
    allCategories,
    filteredAndSortedItems,
    handleSearchChange,
    handleLibraryToggle,
    handleCategoryToggle,
    handleShowOnlyLibrary,
    handleShowOnlyCategory,
    handleResetLibraries,
    handleResetCategories,
    handleResetAllFilters,
    handleToggleStarredOnly,
    handleSortChange,
  } = useComponentFilters({
    initialItems,
    initialFilter,
    starredItemsSet, // Pass the set to the filter hook
  });

  // Defer the filtered list update to keep UI responsive
  const deferredFilteredAndSortedItems = useDeferredValue(
    filteredAndSortedItems
  );

  // Scroll and header collapse logic
  const { isPageScrolled, isFullyCollapsed, toggleFullyCollapsed } =
    useScrollCollapse({
      initiallyScrolled,
      alwaysScrolled,
      initiallyFullyCollapsed,
    });

  // Grid layout measurement and calculations
  const [measureRef, bounds] = useMeasure();
  const {
    columnCount,
    actualColumnWidth,
    rowCount,
    totalGridHeight,
    GAP,
    ROW_HEIGHT,
  } = useGridCalculations({ bounds, items: deferredFilteredAndSortedItems });

  // --- Event Handlers --- //

  // Handler for the global demo toggle switch
  const handleToggleDemos = useCallback((checked: boolean) => {
    setShowDemosGlobally(checked);
  }, []);

  // --- Cell Renderer for react-window --- //

  // Define the type for itemData explicitly
  interface GridItemData {
    items: Item[];
    previews: { [key: string]: ComponentPreviewData }; // Correct type for previews
    showDemos: boolean;
    toggleStar: (id: string) => void;
    isStarred: (id: string) => boolean;
    columnCount: number;
    activeCategories: string[]; // Add activeCategories
    GAP: number;
  }

  // Memoize itemData to prevent unnecessary re-renders of the grid cells
  const itemData: GridItemData = useMemo(
    () => ({
      items: deferredFilteredAndSortedItems,
      previews: preRenderedPreviews, // Ensure this matches the prop type
      showDemos: showDemosGlobally,
      toggleStar: toggleStar,
      isStarred: isStarred, // Pass the check function
      columnCount: columnCount,
      activeCategories: activeCategories, // Include activeCategories
      GAP,
    }),
    [
      deferredFilteredAndSortedItems,
      preRenderedPreviews,
      showDemosGlobally,
      toggleStar,
      isStarred,
      columnCount,
      activeCategories, // Add dependency
      GAP,
    ]
  );

  // Cell component for FixedSizeGrid
  const Cell = useCallback(
    ({
      columnIndex,
      rowIndex,
      style,
      data,
    }: {
      columnIndex: number;
      rowIndex: number;
      style: React.CSSProperties;
      data: GridItemData; // Use the explicitly defined GridItemData type
    }) => {
      const index = rowIndex * data.columnCount + columnIndex;
      if (index >= data.items.length) {
        return null; // Render nothing if index is out of bounds
      }
      const item = data.items[index];
      // Use ?. optional chaining for safety
      const previewDataItem = data.previews?.[item.id];
      const isItemStarred = data.isStarred(item.id);

      const isLastColumn = columnIndex === data.columnCount - 1;

      // Adjust cell style to include padding/gap
      const cellStyle: React.CSSProperties = {
        ...style,
        // Apply right padding unless it's the last column
        padding: `${data.GAP}px`,
        paddingTop: `${data.GAP}px`,
        // Ensure box-sizing is border-box to include padding in width/height
        boxSizing: "border-box",
      };

      // Ensure the imported and memoized ComponentCard is used (aliased as ComponentCard)
      return (
        <ComponentCard
          key={item.id} // Key is still important for React
          item={item}
          previewData={previewDataItem}
          showDemo={data.showDemos}
          toggleStar={data.toggleStar}
          isItemStarred={isItemStarred}
          style={cellStyle} // Pass calculated style
        />
      );
    },
    [] // Keep dependency array empty, rely on itemData memoization
  );

  // --- Render --- //

  return (
    // Use pl-6 from original layout
    <div className="grow flex flex-col w-full pl-6">
      <ComponentBrowserHeader
        // Filter state & handlers
        libraries={allLibraries}
        categories={allCategories}
        activeLibraries={activeLibraries}
        activeCategories={activeCategories}
        onLibraryToggle={handleLibraryToggle}
        onCategoryToggle={handleCategoryToggle}
        onShowOnlyLibrary={handleShowOnlyLibrary}
        onShowOnlyCategory={handleShowOnlyCategory}
        onResetLibraries={handleResetLibraries}
        onResetCategories={handleResetCategories}
        onResetAllFilters={handleResetAllFilters}
        availableLibraries={allLibraries} // Pass available lists
        availableCategories={allCategories}
        components={initialItems} // Pass original items or filtered length
        // Scroll/collapse state & handlers
        isPageScrolled={isPageScrolled}
        isFullyCollapsed={isFullyCollapsed}
        toggleFullyCollapsed={toggleFullyCollapsed}
        // Demo toggle state & handler
        showDemos={showDemosGlobally}
        onToggleDemos={handleToggleDemos}
        // Pass search, sort, starred state/handlers
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        showStarredOnly={showStarredOnly}
        onToggleStarredOnly={handleToggleStarredOnly}
        starredItemsCount={starredItemsSet.size} // Pass the count
        sortOption={sortOption}
        onSortChange={handleSortChange}
      />

      {/* Grid Container - measured for width calculation */}
      <div ref={measureRef} className="relative w-full flex-grow py-6">
        {/* Render FixedSizeGrid only when bounds are measured */}
        {bounds.width > 0 ? (
          <FixedSizeGrid
            columnCount={columnCount}
            columnWidth={actualColumnWidth}
            rowCount={rowCount}
            rowHeight={ROW_HEIGHT} // Use calculated row height
            width={bounds.width} // Use measured width
            height={totalGridHeight} // Use calculated total height
            itemData={itemData} // Pass memoized item data
            style={{ overflow: "visible" }} // Allow shadows/hovers to overflow
            // Pass the Cell component function directly as children
          >
            {Cell}
          </FixedSizeGrid>
        ) : (
          // Placeholder or loading state while measuring
          <SuspenseFallback />
        )}
      </div>
    </div>
  );
}
